import importService from '@/modules/import/services/importService';

const ERROR_PAGE_SIZE = 25;

const initialImportJobState = {
  uid: null,
  progress: null,
  fileName: null,
  status: null,
  type: null,
  dataRowIndex: 0,
  selectedTaxes: [],
  isTaxesUpdated: false,
  taxUpdateError: false,
};

const initialState = {
  importJobState: { ...initialImportJobState },
  errorItemsCurrentPage: 1,
  errorItemsCompleted: false,
  errorItemsLoading: false,
  errorImportJobItems: [],
};

const state = { ...initialState };

const mutations = {
  setImportJobStatus(state, importJobStatus) {
    state.importJobState.status = importJobStatus;
  },
  setImportJobProgress(state, importJobProgress) {
    state.importJobState.progress = importJobProgress;
  },
  setImportJobState(state, importJobState) {
    state.importJobState = { ...state.importJobState, ...importJobState };
  },
  initImportJobState(state) {
    state.importJobState = { ...initialImportJobState };
    state.errorItemsCurrentPage = 1;
    state.errorItemsCompleted = false;
    state.errorItemsLoading = false;
    state.errorImportJobItems = [];
  },
  setErrorItemsLoading(state, loading) {
    state.errorItemsLoading = loading;
  },
  setErrorItemsCompleted(state, completed) {
    state.errorItemsCompleted = completed;
  },
  addErrorImportJobItems(state, errorItems) {
    state.errorImportJobItems = state.errorImportJobItems.concat(errorItems);
  },
  increaseErrorItemsCurrentPage(state) {
    state.errorItemsCurrentPage += 1;
  },
  resetErrorPagination(state) {
    state.errorItemsCurrentPage = 1;
    state.errorItemsCompleted = false;
    state.errorItemsLoading = false;
    state.errorImportJobItems = [];
  },
  setImportJobMetadata(state, metadata) {
    state.importJobState.dataRowIndex = metadata.data_row_index || 0;
  },
  setImportJobSelectedTaxes(state, selectedTaxes) {
    state.importJobState.selectedTaxes = selectedTaxes;
    state.importJobState.isTaxesUpdated = true;
  },
  setIsTaxesUpdated(state, isTaxesUpdated) {
    state.importJobState.isTaxesUpdated = isTaxesUpdated;
  },
  setTaxUpdateError(state, hasError) {
    state.importJobState.taxUpdateError = hasError;
  },
  setImportJobUid(state, uid) {
    state.importJobState.uid = uid;
  },
};

const actions = {
  downloadTemplateFile({ rootGetters }) {
    const params = {
      currency: rootGetters['PaymentSettingsStore/currency'] || 'USD',
      locale: rootGetters['BusinessStore/locale'] || 'en',
    };
    importService.downloadTemplate('product', 'import_products.xlsx', { ...params })
      .then(() => {
        console.log('Template file downloaded successfully');
      })
      .catch((error) => {
        console.error('Error downloading the template file:', error);
      });
  },
  async uploadFile({ commit, rootGetters }, fileToUpload) {
    commit('initImportJobState');
    commit('resetErrorPagination');
    commit('setImportJobStatus', 'uploading');
    const responseStatus = {
      success: false,
      data: null,
      error: null,
    };

    try {
      const response = await importService.uploadFile('product', fileToUpload.file, 'excel', rootGetters['BusinessStore/locale']);
      if (response.success) {
        const uploadState = { ...initialImportJobState };
        uploadState.uid = response.data.uid;
        uploadState.fileName = response.data.provider_data.original_file_name;
        uploadState.status = response.data.status;
        commit('setImportJobState', uploadState);
        responseStatus.success = true;
        responseStatus.data = response.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (e) {
      console.error('Error uploading the file:', e);
      responseStatus.error = e;
      responseStatus.success = false;
      commit('initImportJobState');
    }
    return responseStatus;
  },
  async getImportJobStatus({ commit, state }) {
    try {
      const validationStatus = await importService.importJobStatus(state.importJobState.uid);
      if (validationStatus.success) {
        commit('setImportJobStatus', validationStatus.data.status);
        commit('setImportJobProgress', validationStatus.data.progress);
        commit('setImportJobMetadata', validationStatus.data.metadata);
      } else {
        commit('initImportJobState');
      }
      return validationStatus.data;
    } catch (e) {
      console.log('Error getting the import job status:', e);
      commit('initImportJobState');
      return {
        status: 'error',
        error: e,
      };
    }
  },
  clearImportJobState({ commit }) {
    commit('initImportJobState');
    commit('resetErrorPagination');
  },
  clearImportJobSelectedTaxes({ commit }) {
    commit('setImportJobSelectedTaxes', []);
  },
  setImportJobSelectedTaxes({ commit }, selectedTaxes) {
    commit('setImportJobSelectedTaxes', selectedTaxes);
  },
  setIsTaxesUpdated({ commit }, isTaxesUpdated) {
    commit('setIsTaxesUpdated', isTaxesUpdated);
  },
  async executImportJob({ commit, state }) {
    commit('setImportJobStatus', null);
    try {
      const response = await importService.executeImportJob('product', state.importJobState.uid);
      if (response.success) {
        commit('setImportJobStatus', response.data.status);
      } else {
        throw new Error(response.data.message);
      }
    } catch (e) {
      commit('setImportJobStatus', 'error');
      console.log('Error executing the import job:', e);
    }
  },
  async importJobAddTaxes({ state, commit }) {
    try {
      const result = {
        success: false,
        error: null,
        data: null,
      };
      // Clear previous error state -  each next tried to create import job with taxes
      commit('setTaxUpdateError', false);
      
      const taxIds = state.importJobState.selectedTaxes.map((tax) => tax.id) || [];
      const sharedEntityData = {
        tax_ids: taxIds,
      };
      const response = await importService.createImportJob('product', 'import_job', state.importJobState.uid, 'validate', null, sharedEntityData);
      
      if (response.success) {
        const pollForCompletion = async (uid) => {
          const checkStatus = async () => {
            const jobStatus = await importService.importJobStatus(uid);
            if (jobStatus.data.status === 'done' || jobStatus.data.status === 'error') {
              return jobStatus;
            }

            await new Promise((resolve) => {
              setTimeout(() => resolve(), 1000);
            });
            
            return checkStatus();
          };
          return checkStatus();
        };
        
        const finalStatus = await pollForCompletion(response.data.uid);
        if (finalStatus.data.status === 'done') {
          commit('setImportJobUid', response.data.uid);
          commit('setIsTaxesUpdated', false);
          result.success = true;
          result.data = response.data;
          result.data.taxCount = taxIds.length;
        } else {
          commit('setTaxUpdateError', true);
          result.error = finalStatus.error || 'Job failed to complete';
        }
      } else {
        commit('setTaxUpdateError', true);
        result.error = response.error || 'Failed to create import job';
      }
      return result;
    } catch (e) {
      console.log('Error updating import job with taxes:', e);
      commit('setTaxUpdateError', true);
      throw e;
    }
  },
  async loadMoreErrorItems({ commit, state }) {
    if (state.errorItemsCompleted || state.errorItemsLoading) return;
    commit('setErrorItemsLoading', true);

    try {
      const response = await importService.importJobItems(state.importJobState.uid, state.errorItemsCurrentPage, ERROR_PAGE_SIZE, { status: 'error' });
      
      if (response.success) {
        const errorItems = response.data.import_job_items || [];
        commit('addErrorImportJobItems', errorItems);
        commit('increaseErrorItemsCurrentPage');
        
        if (errorItems.length < ERROR_PAGE_SIZE) {
          commit('setErrorItemsCompleted', true);
        }
      } else {
        commit('setErrorItemsCompleted', true);
      }
    } catch (error) {
      console.log('Error loading more error items:', error);
      commit('setErrorItemsCompleted', true);
    } finally {
      commit('setErrorItemsLoading', false);
    }
  },
  resetErrorPagination({ commit }) {
    commit('resetErrorPagination');
  },
};

const getters = {
  importJobState: (state) => state.importJobState,
  errorItemsLoading: (state) => state.errorItemsLoading,
  errorItemsCompleted: (state) => state.errorItemsCompleted,
  errorImportJobItems: (state) => state.errorImportJobItems,
  errorItemsCurrentPage: (state) => state.errorItemsCurrentPage,
  dataRowIndex: (state) => state.importJobState.dataRowIndex,
  importJobSelectedTaxes: (state) => state.importJobState.selectedTaxes,
  isTaxesUpdated: (state) => state.importJobState.isTaxesUpdated,
  taxUpdateError: (state) => state.importJobState.taxUpdateError,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}; 
